import type { DeductionCategoryCode } from './categories';
import { validateDeductionDefinitionShape, type FieldIssue } from './rules';
import {
  SUPPORTED_CALCULATION_TYPE,
  type ArrearsBehavior, type CatchUpBehavior, type DeductionCalculationType, type DeductionCatalogItem, type DeductionClassification, type DeductionDefinition,
  type DeductionDefinitionInput, type PayrollFrequency, type StopRule,
} from './types';

/** The deduction-definition form: text-friendly state converted to the API input on submit. Fields a category does not use are sent as null or their
 * neutral value (a garnishment carries no amount, limit, priority or stop rule here: Phase 1 adds no limit or priority logic). */

export interface DefinitionFormState {
  name: string;
  shortCode: string;
  categoryCode: DeductionCategoryCode;
  typeCode: string;
  description: string;
  classification: DeductionClassification;
  calculationType: DeductionCalculationType;
  frequency: PayrollFrequency;
  defaultAmount: string;
  defaultCompanyContribution: string;
  effectiveStartDate: string;
  effectiveEndDate: string;
  annualLimit: string;
  perPayPeriodLimit: string;
  goalBalance: string;
  priority: string;
  arrearsBehavior: ArrearsBehavior;
  catchUpBehavior: CatchUpBehavior;
  stopRule: StopRule;
  associatedBenefitCodeId: string;
  requiresEmployeeAuthorization: boolean;
  remittanceRecipientName: string;
  employeeVisibleLabel: string;
  internalNotes: string;
  taxProfileCode: string;
  limitRuleCode: string;
  isActive: boolean;
}

export function isGarnishmentCategory(category: DeductionCategoryCode): boolean {
  return category === 'garnishments-levies';
}

export function emptyDefinitionForm(category: DeductionCategoryCode): DefinitionFormState {
  const garnishment = isGarnishmentCategory(category);
  return {
    name: '', shortCode: '', categoryCode: category, typeCode: '', description: '', classification: garnishment ? 'Involuntary' : 'Voluntary',
    calculationType: garnishment ? 'ApprovedGarnishmentFormula' : SUPPORTED_CALCULATION_TYPE, frequency: 'EveryPayroll', defaultAmount: '', defaultCompanyContribution: '',
    effectiveStartDate: '', effectiveEndDate: '', annualLimit: '', perPayPeriodLimit: '', goalBalance: '', priority: '', arrearsBehavior: 'None', catchUpBehavior: 'None',
    stopRule: 'None', associatedBenefitCodeId: '', requiresEmployeeAuthorization: false, remittanceRecipientName: '', employeeVisibleLabel: '', internalNotes: '',
    taxProfileCode: '', limitRuleCode: '', isActive: false,
  };
}

const text = (value: string | null): string => value ?? '';
const numText = (value: number | null): string => (value === null ? '' : String(value));

export function definitionToForm(def: DeductionDefinition): DefinitionFormState {
  const category = (def.categoryCode ?? 'other-custom') as DeductionCategoryCode;
  return {
    name: def.name, shortCode: text(def.shortCode), categoryCode: category, typeCode: text(def.typeCode), description: text(def.description),
    classification: def.classification ?? 'Voluntary', calculationType: def.calculationType ?? (def.amountType === 2 ? 'PercentOfEligibleGross' : 'FixedAmount'),
    frequency: def.frequency ?? 'EveryPayroll', defaultAmount: numText(def.defaultAmount), defaultCompanyContribution: numText(def.defaultCompanyContribution),
    effectiveStartDate: text(def.effectiveStartDate), effectiveEndDate: text(def.effectiveEndDate), annualLimit: numText(def.annualLimit),
    perPayPeriodLimit: numText(def.perPayPeriodLimit), goalBalance: numText(def.goalBalance), priority: numText(def.priority),
    arrearsBehavior: def.arrearsBehavior ?? 'None', catchUpBehavior: def.catchUpBehavior ?? 'None', stopRule: def.stopRule ?? 'None',
    associatedBenefitCodeId: text(def.associatedBenefitCodeId), requiresEmployeeAuthorization: def.requiresEmployeeAuthorization ?? false,
    remittanceRecipientName: text(def.remittanceRecipientName), employeeVisibleLabel: text(def.employeeVisibleLabel), internalNotes: text(def.internalNotes),
    taxProfileCode: text(def.taxProfileCode), limitRuleCode: text(def.limitRuleCode), isActive: def.isActive,
  };
}

/** Prefill from a catalog type: name, description, classification and the type's usual calculation. */
export function applyDeductionCatalogType(form: DefinitionFormState, item: DeductionCatalogItem): DefinitionFormState {
  return {
    ...form, typeCode: item.code, categoryCode: item.categoryCode, name: item.name, description: item.description, classification: item.classification,
    calculationType: item.defaultCalculationType ?? SUPPORTED_CALCULATION_TYPE,
  };
}

export function suggestDeductionShortCode(name: string): string {
  const code = name.toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30).replace(/-+$/g, '');
  return code.length >= 2 ? code : '';
}

export function isPercentType(type: DeductionCalculationType): boolean {
  return type === 'PercentOfEligibleGross' || type === 'PercentOfDisposableEarnings' || type === 'ApprovedRetirementElectionPercent';
}

/** Why a definition cannot be active yet, in plain words, or null when it can. The server decides; this only explains the checkbox. */
export function activationBlocker(form: Pick<DefinitionFormState, 'categoryCode' | 'calculationType' | 'taxProfileCode'>): string | null {
  if (isGarnishmentCategory(form.categoryCode)) return 'Garnishment limits and priorities are controlled by applicable rules. A garnishment needs rule review and cannot be activated here.';
  if (form.calculationType !== SUPPORTED_CALCULATION_TYPE) return 'Percentage and other calculation types cannot be activated yet. It will be saved inactive.';
  if (form.taxProfileCode === '') return 'Tax treatment must be approved before this deduction can be used in payroll. It will be saved inactive until a tax profile is approved.';
  return null;
}

function parseNumber(raw: string, field: string, issues: FieldIssue[]): number | null {
  const t = raw.trim();
  if (t === '') return null;
  if (!/^-?\d*\.?\d+$/.test(t)) {
    issues.push({ field, message: 'Enter a number, for example 50.25.' });
    return null;
  }
  return Number(t);
}

const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

export function formToDefinitionInput(form: DefinitionFormState): { input: DeductionDefinitionInput; issues: FieldIssue[] } {
  const parseIssues: FieldIssue[] = [];
  const garnishment = isGarnishmentCategory(form.categoryCode);
  const blocker = activationBlocker(form);
  const input: DeductionDefinitionInput = {
    name: form.name.trim(),
    shortCode: form.shortCode.trim(),
    categoryCode: form.categoryCode,
    typeCode: orNull(form.typeCode),
    description: orNull(form.description),
    classification: garnishment ? 'Involuntary' : form.classification,
    calculationType: garnishment ? 'ApprovedGarnishmentFormula' : form.calculationType,
    frequency: form.frequency,
    defaultAmount: garnishment ? null : parseNumber(form.defaultAmount, 'defaultAmount', parseIssues),
    defaultCompanyContribution: garnishment ? null : parseNumber(form.defaultCompanyContribution, 'defaultCompanyContribution', parseIssues),
    effectiveStartDate: orNull(form.effectiveStartDate),
    effectiveEndDate: orNull(form.effectiveEndDate),
    annualLimit: garnishment ? null : parseNumber(form.annualLimit, 'annualLimit', parseIssues),
    perPayPeriodLimit: garnishment ? null : parseNumber(form.perPayPeriodLimit, 'perPayPeriodLimit', parseIssues),
    goalBalance: garnishment ? null : parseNumber(form.goalBalance, 'goalBalance', parseIssues),
    priority: garnishment ? null : parseNumber(form.priority, 'priority', parseIssues),
    arrearsBehavior: garnishment ? 'None' : form.arrearsBehavior,
    catchUpBehavior: garnishment ? 'None' : form.catchUpBehavior,
    stopRule: garnishment ? 'None' : form.stopRule,
    associatedBenefitCodeId: orNull(form.associatedBenefitCodeId),
    requiresEmployeeAuthorization: form.requiresEmployeeAuthorization,
    remittanceRecipientName: orNull(form.remittanceRecipientName),
    employeeVisibleLabel: orNull(form.employeeVisibleLabel),
    internalNotes: orNull(form.internalNotes),
    taxProfileCode: orNull(form.taxProfileCode),
    limitRuleCode: orNull(form.limitRuleCode),
    // A definition that cannot be active is saved inactive; the server would refuse an explicit activation of it.
    isActive: form.isActive && blocker === null,
  };
  const parsed = new Set(parseIssues.map((i) => i.field));
  const ruleIssues = validateDeductionDefinitionShape(input).filter((i) => !parsed.has(i.field));
  return { input, issues: [...parseIssues, ...ruleIssues] };
}
